import React, {
	useMemo,
	useState,
} from 'react';
import {
	Card,
	ConfigProvider,
	Descriptions,
	Flex,
	Input,
	Modal as AntdModal,
} from 'antd';
import { useQueryClient } from 'react-query';
import { EstimateStatus, VesselOwnershipTypes } from '@shared/utils/constants';
import Button from '@client/components/Button';
import ConsumptionSets from '@client/screens/fleet/VesselDetailsScreen/components/ConsumptionSets/ConsumptionSets';
import FuelEuCalculations from '@client/screens/estimates/fuelEU/FuelEuCalculations';
import EstimateCosts from '@client/screens/estimates/costs/EstimateCosts';
import { updateFixture } from '@client/lib/api';
import UsersBar from '@client/components/UserPickerSelect/UsersBar';
import Modal from '@client/components/Modal/Modal';
import { useEstimate } from '../context/EstimatorContext';
import estimateConfigurationItems from '../helpers/estimateConfigurationItems';
import { useCentralStore } from '../context/CentralStoreContext';
import { useEstimateChangesProvider } from '../context/EstimateChangesContext';
import { useCargoProvider } from '../context/CargoContext';
import styles from './styles/EstimateConfigurationCard.module.css';
import LinkContractEstimateModal from './LinkContractEstimateModal';

const EstimateConfigurationCard = () => {
	const [showConsumptionModal, setShowConsumptionModal] = useState(false);
	const [showFuelEuModal, setShowFuelEuModal] = useState(false);
	const [showEstimateCostsModal, setShowEstimateCostsModal] = useState(false);
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [blockClose, setBlockClose] = useState(false);
	const [editEstimateCostMode, setEditEstimateCostMode] = useState(false);

	const {
		estimate,
		vessel,
		refreshVessel,
		onUpdateEstimate,
		onRefreshEuaPriceMutator,
		handleMiscCostChange,
		estimateCosts,
		refreshEstimateCosts,
	} = useEstimate();

	const disabledLinkButton = useMemo(() => {
		return !vessel || (vessel.ownershipType !== VesselOwnershipTypes.OWNED && vessel.ownershipType !== VesselOwnershipTypes.TC_IN);
	}, [vessel]);

	const {
		refreshEverything,
	} = useCentralStore();

	const {
		hasCargoRelet
	} = useCargoProvider();

	const queryClient = useQueryClient();
	const { componentDisabled } = ConfigProvider.useConfig();

	const items = useMemo(() => estimateConfigurationItems({
		estimate,
		vessel,
		onUpdateEstimate,
		onRefreshEuaPriceMutator,
		queryClient,
		setShowFuelEuModal,
		setShowEstimateCostsModal,
		handleMiscCostChange,
		estimateCosts,
		refreshEstimateCosts,
		hasCargoRelet,
		componentDisabled,
	}), [
		estimate,
		vessel,
		onUpdateEstimate,
		onRefreshEuaPriceMutator,
		queryClient,
		handleMiscCostChange,
		estimateCosts,
		refreshEstimateCosts,
		hasCargoRelet,
		componentDisabled,
	]);

	const {
		useBlocker,
		makeBlockable,
	} = useEstimateChangesProvider();

	useBlocker(blockClose);

	const onCloseModal = makeBlockable(() => setShowConsumptionModal(false));
	const onCloseFuelEuModal = makeBlockable(() => setShowFuelEuModal(false));
	const onCloseLinkModal = makeBlockable(() => setShowLinkModal(false));
	const onCloseEstimateCostsModal = makeBlockable(async () => {
		if (editEstimateCostMode) {
			warnEstimateCostCancel();

			return;
		}

		setShowEstimateCostsModal(false);
		await refreshEstimateCosts();
	});

	const isFixed = estimate?.status === EstimateStatus.Fixed;
	const warnEstimateCostCancel = () => {
		if (editEstimateCostMode) {
			AntdModal.warning({
				title: 'Editing in progress',
				content: 'Please save or cancel your changes before closing.',
			});
		}
	};

	const handleUserChange = async (
		field: 'fixedBy' | 'operatedBy',
		value: number | undefined,
	) => {
		await onUpdateEstimate({ [field]: value ?? null });
		if (isFixed && estimate?.fixtureId != null) {
			await updateFixture(estimate?.fixtureId, { [field]: value ?? null });
		}

		await refreshEverything();
	};

	return (
		<>
			<Card
				size="small"
				className={styles.card}
				styles={{ body: { padding: 0 } }}
				title={(
					<Input
						size="small"
						className={styles.comment}
						placeholder="Est. comment"
						disabled={isFixed}
						value={estimate?.comment ?? ''}
						onChange={(e) => onUpdateEstimate({ comment: e.target.value })}
						data-testid="estimateComment"
					/>
				)}
				extra={(
					<UsersBar
						fixedBy={estimate?.fixedBy}
						operatedBy={estimate?.operatedBy}
						onChangeFixedBy={(v) => handleUserChange('fixedBy', v)}
						onChangeOperatedBy={(v) => handleUserChange('operatedBy', v)}
						extra={(
							<Flex gap={10}>
								<Button
									disabled={disabledLinkButton || hasCargoRelet}
									size="small"
									onClick={() => setShowLinkModal(true)}
								>
									Import prev. contract
								</Button>
								<Button
									disabled={isFixed || hasCargoRelet}
									onClick={() => setShowConsumptionModal(true)}
									size="small"
								>
									Set consumption details
								</Button>
							</Flex>
						)}
					/>
				)}
			>
				<Descriptions className={styles.desc} size="small" bordered items={items} />
			</Card>
			<Modal
				open={showConsumptionModal}
				footer={null}
				style={{ top: 20 }}
				onCancel={onCloseModal}
				okButtonProps={{
					onClick: onCloseModal,
				}}
				width="75%"
			>
				<ConsumptionSets
					refreshVessel={refreshVessel}
					activeConsumptionSetId={estimate?.activeConsumptionSetId}
					inPortEntries={vessel?.inPortEntries}
					setEditing={setShowConsumptionModal}
					onRefresh={async () => {
						await refreshEverything();
					}}
					setBlockClose={setBlockClose}
					vessel={vessel}
				/>
			</Modal>
			{estimate?.vesselId != null && (
				<Modal
					open={showLinkModal}
					footer={null}
					onCancel={onCloseLinkModal}
					okButtonProps={{
						onClick: onCloseLinkModal,
					}}
					width={500}
					destroyOnClose
				>
					<LinkContractEstimateModal />
				</Modal>
			)}
			{estimate != null && (
				<>
					<Modal
						open={showFuelEuModal}
						footer={null}
						className={styles.fuelEuPenaltyModal}
						onCancel={onCloseFuelEuModal}
						okButtonProps={{
							onClick: onCloseFuelEuModal,
						}}
						width="75%"
					>
						<FuelEuCalculations estimate={estimate} />
					</Modal>
					<Modal
						open={showEstimateCostsModal}
						footer={null}
						className={styles.fuelEuPenaltyModal}
						onCancel={onCloseEstimateCostsModal}
						afterClose={onCloseEstimateCostsModal}
						okButtonProps={{
							onClick: onCloseEstimateCostsModal,
						}}
						width="75%"
					>
						<EstimateCosts
							estimate={estimate}
							estimateCosts={estimateCosts}
							onUpdateEstimate={onUpdateEstimate}
							refreshEstimateCosts={refreshEstimateCosts}
							setEditEstimateCostMode={setEditEstimateCostMode}
						/>
					</Modal>
				</>
			)}
		</>

	);
};

export default EstimateConfigurationCard;
